import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Candidates from './Candidates';
import Clients from './Clients';
import Jobs from './Jobs';
import JobSheets from './JobSheets';
import Training from './Training';
import JtkReport from './JtkReport';

const NAV = [
  {
    section: 'Recruitment',
    items: [
      { label: 'Candidates',               icon: '◉', key: 'candidates' },
      { label: 'Sent Profiles & Interviews', icon: '◑', key: 'jobsheets' },
      { label: 'Jobs',                     icon: '◎', key: 'jobs' },
      { label: 'Clients',                  icon: '◇', key: 'clients' },
    ]
  },
  {
    section: 'Training Academy',
    items: [
      { label: 'Programmes',  icon: '◧', key: 'training' },
    ]
  },
  {
    section: 'System',
    items: [
      { label: 'Reports',     icon: '◐', key: 'reports' },
      { label: 'Settings',    icon: '◌', key: 'settings' },
    ]
  }
];

function StatCard({ label, value, note, onClick }) {
  return (
    <div onClick={onClick}
      className={`bg-white border border-carriera-border p-5 ${onClick ? 'cursor-pointer hover:border-carriera-accent transition-colors duration-200' : ''}`}
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <p className="text-xs text-carriera-muted uppercase tracking-widest mb-2">{label}</p>
      <p className="font-serif text-3xl font-normal text-carriera-text mb-1">{value}</p>
      {note && <p className="text-xs text-carriera-muted">{note}</p>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [page, setPage]           = useState('overview');
  const [stats, setStats]         = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (page === 'overview') fetchStats();
  }, [page]);

  async function fetchStats() {
    try {
      const [apps, jobs, jobSheets, trainings] = await Promise.all([
        axios.get('https://carriera-backend.onrender.com/api/candidates', { headers }),
        axios.get('https://carriera-backend.onrender.com/api/jobs', { headers }),
        axios.get('https://carriera-backend.onrender.com/api/job-sheets', { headers }),
        axios.get('https://carriera-backend.onrender.com/api/trainings', { headers }),
      ]);

      const today = new Date(); today.setHours(0,0,0,0);
      const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);

      const allApps = apps.data;
      const newThisWeek = allApps.filter(a => new Date(a.created_at) >= weekAgo).length;
      const active      = allApps.filter(a => a.status === 'active').length;
      const placed      = 0;

      const allJobs = jobs.data;
      const liveJobs = allJobs.filter(j => j.status === 'live').length;

      const todayStr = new Date().toISOString().slice(0, 10);
      const scheduledInterviews = jobSheets.data.filter(
        s => s.status === 'interview_scheduled' && s.interview_date
      );
      const upcomingInterviews = scheduledInterviews.filter(s => s.interview_date >= todayStr).length;
      const todayInterviews = scheduledInterviews.filter(s => s.interview_date === todayStr).length;

      const allTrainings = trainings.data;
      const upcomingTrainings = allTrainings.filter(t => t.status === 'upcoming').length;
      const totalRegs = allTrainings.reduce((sum, t) => sum + (t.training_registrations?.length || 0), 0);
      const activeRegs = allTrainings.reduce((sum, t) => {
        return sum + (t.training_registrations?.filter(r =>
          !['certificates_sent','claim_submitted'].includes(r.stage)
        ).length || 0);
      }, 0);

      setStats({
        recruitment: { newThisWeek, active, placed, liveJobs, upcomingInterviews, todayInterviews },
        training:    { upcomingTrainings, totalRegs, activeRegs }
      });
    } catch {}
  }

  function handleLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-carriera-bg flex">

      {/* Sidebar */}
      <aside className="w-60 bg-carriera-text flex flex-col flex-shrink-0">

        {/* Logo */}
        <div className="px-6 py-5 border-b border-white/10 cursor-pointer" onClick={() => setPage('overview')}>
          <img src="/Carriera_Logo_NoBG_WhiteFont.png" alt="Carriera" style={{ width: 110, height: 'auto', display: 'block' }} />
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">

          {/* Overview */}
          <button onClick={() => setPage('overview')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors duration-200 text-left mb-2 ${
              page === 'overview' ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}>
            <span className="text-carriera-accent">◈</span>
            Overview
          </button>

          {NAV.map(group => (
            <div key={group.section} className="mb-4">
              <p className="text-xs font-semibold tracking-widest uppercase px-3 mb-1"
                 style={{ color: 'rgba(196,180,131,0.6)' }}>
                {group.section}
              </p>
              {group.items.map(item => (
                <button key={item.key} onClick={() => setPage(item.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors duration-200 text-left ${
                    page === item.key ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}>
                  <span className="text-carriera-accent">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="px-4 py-4 border-t border-white/10">
          <p className="text-white/60 text-xs mb-0.5">{user.name || 'User'}</p>
          <p className="text-white/30 text-xs mb-3">{user.email}</p>
          <button onClick={handleLogout}
            className="text-xs text-white/40 hover:text-white transition-colors duration-200">
            Sign out →
          </button>
        </div>

      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 overflow-y-auto">

        {/* ── OVERVIEW ── */}
        {page === 'overview' && (
          <div>
            <div className="mb-8">
              <p className="text-xs font-medium text-carriera-muted uppercase tracking-widest mb-1">Welcome back</p>
              <h2 className="font-serif text-3xl font-normal text-carriera-text">{user.name || 'Dashboard'}</h2>
            </div>

            {/* ── Recruitment Section ── */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-carriera-accent">Recruitment</p>
                <div className="flex-1 border-t border-carriera-border" />
                <p className="text-xs text-carriera-muted">Agensi Pekerjaan Carriera Talent Resources</p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <StatCard label="New Applications" value={stats?.recruitment.newThisWeek ?? '—'} note="This week" onClick={() => setPage('candidates')} />
                <StatCard label="Active Candidates" value={stats?.recruitment.active ?? '—'} note="In pipeline" onClick={() => setPage('candidates')} />
                <StatCard label="Placed" value={stats?.recruitment.placed ?? '—'} note="All time" onClick={() => setPage('talent')} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <StatCard label="Live Positions" value={stats?.recruitment.liveJobs ?? '—'} note="Open jobs" onClick={() => setPage('jobs')} />
                <StatCard label="Interviews Today" value={stats?.recruitment.todayInterviews ?? '—'} note="Scheduled" onClick={() => setPage('jobsheets')} />
                <StatCard label="Upcoming Interviews" value={stats?.recruitment.upcomingInterviews ?? '—'} note="All upcoming" onClick={() => setPage('jobsheets')} />
              </div>
            </div>

            {/* ── Training Section ── */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-carriera-accent">Training Academy</p>
                <div className="flex-1 border-t border-carriera-border" />
                <p className="text-xs text-carriera-muted">Carriera Academy Sdn Bhd</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <StatCard label="Upcoming Programmes" value={stats?.training.upcomingTrainings ?? '—'} note="Scheduled" onClick={() => setPage('training')} />
                <StatCard label="Active Registrations" value={stats?.training.activeRegs ?? '—'} note="In progress" onClick={() => setPage('training')} />
                <StatCard label="Total Registrations" value={stats?.training.totalRegs ?? '—'} note="All time" onClick={() => setPage('training')} />
              </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border border-carriera-border p-6" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <p className="font-serif text-lg text-carriera-text mb-1">Recruitment Pipeline</p>
                <p className="text-sm text-carriera-muted mb-4">Manage candidates, schedule interviews and track placements.</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage('candidates')}
                    className="text-xs bg-carriera-text text-white px-3 py-1.5 hover:bg-carriera-accent transition-colors duration-200">
                    Candidates
                  </button>
                  <button onClick={() => setPage('jobsheets')}
                    className="text-xs border border-carriera-border text-carriera-sec px-3 py-1.5 hover:border-carriera-accent transition-colors duration-200">
                    Sent Profiles
                  </button>
                </div>
              </div>

              <div className="bg-white border border-carriera-border p-6" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <p className="font-serif text-lg text-carriera-text mb-1">Training Academy</p>
                <p className="text-sm text-carriera-muted mb-4">Manage programmes, track registrations and grant progress.</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage('training')}
                    className="text-xs bg-carriera-text text-white px-3 py-1.5 hover:bg-carriera-accent transition-colors duration-200">
                    Programmes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {page === 'candidates'  && <Candidates />}
        {page === 'jobsheets'   && <JobSheets />}
        {page === 'clients'     && <Clients />}
        {page === 'jobs'        && <Jobs />}
        {page === 'training'    && <Training />}
        {page === 'reports'     && <JtkReport />}

        {!['overview','candidates','jobsheets','clients','jobs','training','reports'].includes(page) && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="font-serif text-2xl text-carriera-text mb-2 capitalize">{page}</p>
              <p className="text-sm text-carriera-muted">Coming soon.</p>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
